import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, ExternalLink, ArrowLeft, RefreshCw, Maximize2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function HRPortalEmbed() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [iframeError, setIframeError] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    const token = localStorage.getItem('authToken');
    
    if (!userData || !token) {
      navigate('/login');
      return;
    }

    const user = JSON.parse(userData);
    
    // Check if user has HR access
    const hasHRAccess = user.products?.some(p => p.id === 'hr');
    if (!hasHRAccess) {
      navigate('/portal');
      return;
    }

    // Generate SSO URL for iframe embed
    const generateSSOUrl = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL + '/api';
        const response = await fetch(`${API}/sso/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ target_app: 'hr' })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Set iframe URL with SSO token (using hash for client-side handling)
          const hrPortalUrl = process.env.REACT_APP_HR_PORTAL_URL || 'https://myhrorbit.com';
          setIframeUrl(`${hrPortalUrl}/#sso_token=${data.sso_token}`);
          setLoading(false);
        } else {
          setError('Failed to generate SSO token');
          setLoading(false);
        }
      } catch (err) {
        console.error('SSO generation failed:', err);
        setError('Connection error. Please try again.');
        setLoading(false);
      }
    };

    generateSSOUrl();
  }, [navigate]);

  const handleOpenInNewTab = () => {
    if (iframeUrl) {
      window.open(iframeUrl, '_blank');
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    setIframeError(false);
    window.location.reload();
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ExternalLink className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Connection Error</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={handleRefresh}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Try Again
            </Button>
            <Button 
              variant="outline" 
              onClick={() => window.open(`${process.env.REACT_APP_HR_PORTAL_URL || 'https://myhrorbit.com'}/login`, '_blank')}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Open MyHR Orbit
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Header Bar */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link 
            to="/portal" 
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Portal</span>
          </Link>
          <div className="h-6 w-px bg-slate-700" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-white">MyHR Orbit</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            className="text-slate-400 hover:text-white"
            data-testid="refresh-hr-portal"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleOpenInNewTab}
            className="text-slate-400 hover:text-white"
            data-testid="open-hr-new-tab"
          >
            <Maximize2 className="w-4 h-4 mr-2" />
            Open in New Tab
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-10">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/30">
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Connecting to MyHR Orbit</h2>
              <p className="text-slate-400">Setting up your secure session...</p>
              <div className="mt-8 flex justify-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        {iframeError && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-10">
            <div className="text-center max-w-md p-8">
              <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <ExternalLink className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Unable to Embed</h2>
              <p className="text-slate-400 mb-6">
                MyHR Orbit cannot be displayed in an embedded view due to security settings. 
                Please open it in a new tab for the best experience.
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  onClick={handleOpenInNewTab}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Maximize2 className="w-4 h-4 mr-2" />
                  Open MyHR Orbit
                </Button>
              </div>
            </div>
          </div>
        )}

        {iframeUrl && !iframeError && (
          <iframe
            src={iframeUrl}
            className="w-full h-full border-0"
            title="MyHR Orbit Dashboard"
            onLoad={() => setLoading(false)}
            onError={() => {
              setIframeError(true);
              setLoading(false);
            }}
            allow="clipboard-read; clipboard-write"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
            data-testid="hr-portal-iframe"
          />
        )}
      </div>
    </div>
  );
}
